import { Box, Chip, Divider, List, ListItemButton, ListItemText, Paper, Typography } from "@mui/material";
import { AnalysisStatusLabel } from "Components/Analyses/_Shared/AnalysisStatusLabel";
import { HStack } from "Components/_Shared/Core/Stack";
import { Empty } from "Components/_Shared/Empty";
import { ReactComponent as SearchIcon } from "Components/_Shared/Icons/Assets/search.svg";
import { Loader } from "Components/_Shared/Loader";
import { UserInitialsAvatar } from "Components/_Shared/UserInitialsAvatar";
import type { SearchedClient, SearchedLocalClient } from "Infrastructure/ApiZfp/Schema";
import { useFormatLocale } from "Infrastructure/Hooks/DateFns/useFormatLocale";
import { Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { Fragment } from "react";

type NamedClient = Pick<SearchedClient | SearchedLocalClient, "guid" | "name" | "firstName" | "surname" | "birthDate" | "analysis">;

type Props = {
  isLoading: boolean;
  existingClients: SearchedClient[];
  localClients?: SearchedLocalClient[];
  onSelect: (client: SearchedClient) => void;
  onSelectLocal?: (client: SearchedLocalClient) => void;
  showAnalysisStatus?: boolean;
};

const GroupHeader = ({ label }: { label: string }) => (
  <Box
    px={Spacing.XL}
    py={Spacing.S}
    fontSize="0.75rem"
    fontWeight={600}
    textTransform="uppercase"
    letterSpacing="0.04em"
    color="text.secondary"
    sx={(theme) => ({
      bgcolor: theme.colors.backgroundSecondary,
      borderTop: `1px solid ${theme.colors.border}`,
      borderBottom: `1px solid ${theme.colors.border}`,
    })}
  >
    {label}
  </Box>
);

export const ClientsSearchResults = ({
  isLoading,
  existingClients,
  localClients,
  onSelect,
  onSelectLocal,
  showAnalysisStatus = false,
}: Props) => {
  const isGrouped = localClients !== undefined;
  const hasResults = existingClients.length > 0 || (localClients?.length ?? 0) > 0;

  const { t } = useResource();
  const formatDate = useFormatLocale();

  const renderClientRow = <T extends NamedClient>(client: T, isLast: boolean, onClick: () => void, isMangoUpClient: boolean) => (
    <Fragment key={client.guid}>
      <ListItemButton onClick={onClick}>
        <HStack width="100%" spacing={3} alignItems={"center"} justifyContent="space-between">
          <HStack spacing={3} alignItems={"center"} py={0.5}>
            <UserInitialsAvatar firstName={client.firstName} surname={client.surname} fullName={client.name} variant="small" />
            <ListItemText
              primary={
                <Typography>
                  <Typography component={"span"} fontSize={14} fontWeight={"bold"}>
                    {client.name}
                  </Typography>
                  {client.birthDate && (
                    <Typography component={"span"} pl={1} fontSize={"small"} color={"grey.600"}>
                      ({formatDate(new Date(client.birthDate), "yyyy")})
                    </Typography>
                  )}
                </Typography>
              }
            />
            {showAnalysisStatus && client.analysis && <AnalysisStatusLabel state={client.analysis.state} size="small" />}
          </HStack>
          <HStack spacing={Spacing.L} alignItems={"center"}>
            {isMangoUpClient && <Chip label={t(Resources.Clients.MangoUpChipLabel)} variant="outlined" color="primary" size="small" />}
          </HStack>
        </HStack>
      </ListItemButton>
      {!isLast && <Divider />}
    </Fragment>
  );

  return (
    <Paper
      elevation={4}
      sx={{
        position: "absolute",
        top: "100%",
        left: 0,
        right: 0,
        zIndex: 100,
        maxHeight: "400px",
        scrollBehavior: "smooth",
        overflowX: "hidden",
        borderRadius: 1,
        border: "1px solid",
        borderColor: "grey.300",
      }}
    >
      {isLoading ? (
        <HStack width={"100%"} spacing={2} justifyContent={"center"} alignItems={"center"}>
          <Loader />
          <Typography variant="body2">{t(Resources.Common.Searching)}</Typography>
        </HStack>
      ) : hasResults ? (
        isGrouped ? (
          <>
            {existingClients.length > 0 && (
              <List disablePadding>
                <GroupHeader label={t(Resources.Clients.ExistingClientsGroup)} />
                {existingClients.map((client, index) =>
                  renderClientRow(client, index === existingClients.length - 1, () => onSelect(client), true),
                )}
              </List>
            )}
            {localClients && localClients.length > 0 && (
              <List disablePadding>
                <GroupHeader label={t(Resources.Clients.LocalClientsGroup)} />
                {localClients.map((client, index) =>
                  renderClientRow(client, index === localClients.length - 1, () => onSelectLocal?.(client), false),
                )}
              </List>
            )}
          </>
        ) : (
          <List disablePadding>
            {existingClients.map((client, index) =>
              renderClientRow(client, index === existingClients.length - 1, () => onSelect(client), true),
            )}
          </List>
        )
      ) : (
        <Empty
          title={t(Resources.Clients.Empty.Title)}
          text={t(Resources.Clients.Empty.Text)}
          icon={<SearchIcon width={30} height={30} />}
        />
      )}
    </Paper>
  );
};
